import { CommonModule } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, catchError, of, takeUntil } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { DashboardService } from '../services/dashboard.service';
import {
  Transcription,
  TranscriptionsService,
  isFailedTranscriptionStatus,
} from '../transcriptions/transcriptions.service';

@Component({
  selector: 'app-analytics',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './analytics.component.html',
  styleUrl: './analytics.component.scss',
})
export class AnalyticsComponent implements OnInit, OnDestroy {
  private readonly dashboard = inject(DashboardService);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly transcriptionsService = inject(TranscriptionsService);
  private readonly destroy$ = new Subject<void>();

  readonly loading = signal(true);
  readonly error = signal(false);
  readonly doctorName = signal('Guest');
  readonly searchText = signal('');
  readonly selectedStatus = signal('All');
  readonly statusOpen = signal(false);
  readonly transcriptions = signal<Transcription[]>([]);

  readonly statusOptions = [
    { label: 'All', value: 'All' },
    { label: 'Completed', value: 'Completed' },
    { label: 'Needs Review', value: 'Needs Review' },
    { label: 'In Progress', value: 'In Progress' },
    { label: 'Failed', value: 'Failed' },
  ];

  readonly recentVisits = computed(() => this.transcriptions().slice(0, 3));

  readonly keyMetrics = computed(() => {
    const statusCounts = this.transcriptions().reduce(
      (acc, t) => {
        const display = this.displayStatus(t.status);
        acc[display] = (acc[display] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );
    return {
      needsReview: statusCounts['Needs Review'] || 0,
      inProgress: statusCounts['In Progress'] || 0,
      completed: statusCounts['Completed'] || 0,
    };
  });

  readonly summary = computed(() => {
    const rows = this.transcriptions();
    const uniquePatients = new Set(
      rows.map((t) => t.patientId || t.mrn).filter((id) => id != null && id !== 'N/A' && id !== 'Unknown')
    ).size;

    let totalSeconds = 0;
    for (const t of rows) {
      if (t.duration && t.duration !== 'N/A' && t.duration !== 'Unknown') {
        const parts = t.duration.split(':').map(Number);
        if (parts.length === 3) {
          totalSeconds += parts[0] * 3600 + parts[1] * 60 + parts[2];
        } else if (parts.length === 2) {
          totalSeconds += parts[0] * 60 + parts[1];
        }
      }
    }

    const completedCount = rows.filter((t) => this.displayStatus(t.status) === 'Completed').length;
    const completionRate =
      rows.length > 0 ? Math.round((completedCount / rows.length) * 100 * 10) / 10 : 0;

    return {
      total: rows.length,
      patients: uniquePatients,
      hours: Math.round((totalSeconds / 3600) * 10) / 10,
      completionRate,
    };
  });

  ngOnInit(): void {
    const name = this.auth.getDisplayName();
    this.doctorName.set(name && name.trim() ? name.trim() : 'Guest');
    this.loadDashboardData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.statusOpen.set(false);
  }

  toggleStatusMenu(event: Event): void {
    event.stopPropagation();
    this.statusOpen.update((open) => !open);
  }

  chooseStatus(value: string, event: Event): void {
    event.stopPropagation();
    this.selectedStatus.set(value);
    this.statusOpen.set(false);
  }

  onSearch(): void {
    const queryParams: Record<string, string> = {};
    const search = this.searchText().trim();
    if (search) {
      queryParams['search'] = search;
    }
    const status = this.selectedStatus();
    if (status && status !== 'All') {
      queryParams['status'] = status;
    }
    void this.router.navigate(['/'], { queryParams });
  }

  onViewAllVisits(): void {
    void this.router.navigate(['/']);
  }

  onNewVisit(): void {
    void this.router.navigate(['/'], { queryParams: { new: '1' } });
  }

  onRecentVisitAction(visit: Transcription): void {
    if (this.isPaused(visit)) {
      void this.router.navigate(['/recording'], {
        queryParams: { resumeTranscription: visit.id },
      });
      return;
    }
    if (this.isInProgress(visit) || isFailedTranscriptionStatus(visit.status)) {
      return;
    }
    this.transcriptionsService.requestSelectVisitOnLoad(visit.id);
    void this.router.navigate(['/'], { queryParams: { visit: String(visit.id) } });
  }

  onPatient(visit: Transcription, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const key = visit.mrn || visit.patientId;
    if (!key) {
      return;
    }
    void this.router.navigate(['/patients', key]);
  }

  visitTitle(visit: Transcription): string {
    if (visit.specialty) {
      const specialtyName = visit.specialty
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
      return `${specialtyName} Consultation`;
    }
    return 'Consultation';
  }

  formatDate(dateString: string | undefined): string {
    if (!dateString) {
      return 'N/A';
    }
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return dateString;
    }
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  }

  displayStatus(status: string): string {
    const normalized = status ? status.toLowerCase() : '';
    switch (normalized) {
      case 'completed':
        return 'Completed';
      case 'needs review':
        return 'Needs Review';
      case 'in progress':
      case 'transcribing':
      case 'transcribed':
      case 'analyzing':
        return 'In Progress';
      case 'paused':
        return 'Paused';
      case 'failed':
        return 'Failed';
      default:
        return 'In Progress';
    }
  }

  statusClass(status: string): string {
    switch (this.displayStatus(status)) {
      case 'Completed':
        return 'is-completed';
      case 'Needs Review':
        return 'is-review';
      case 'Failed':
        return 'is-failed';
      case 'Paused':
        return 'is-paused';
      default:
        return 'is-progress';
    }
  }

  isPaused(visit: Transcription): boolean {
    return this.displayStatus(visit.status) === 'Paused';
  }

  isInProgress(visit: Transcription): boolean {
    return !this.isPaused(visit) && this.displayStatus(visit.status) === 'In Progress';
  }

  recentActionLabel(visit: Transcription): string {
    if (this.isPaused(visit)) {
      return 'Resume';
    }
    return this.isInProgress(visit) ? 'Processing' : 'View';
  }

  private loadDashboardData(): void {
    this.loading.set(true);
    this.error.set(false);
    this.dashboard
      .getTranscriptions()
      .pipe(
        catchError((err) => {
          console.error('Error loading analytics:', err);
          return of(null);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((transcriptions) => {
        if (transcriptions == null) {
          this.transcriptions.set([]);
          this.loading.set(false);
          this.error.set(true);
          return;
        }
        const sorted = [...transcriptions].sort((a, b) => {
          const dateTimeA = a.appointmentTime
            ? a.appointmentTime instanceof Date
              ? a.appointmentTime
              : new Date(a.appointmentTime)
            : new Date(a.date);
          const dateTimeB = b.appointmentTime
            ? b.appointmentTime instanceof Date
              ? b.appointmentTime
              : new Date(b.appointmentTime)
            : new Date(b.date);
          return dateTimeB.getTime() - dateTimeA.getTime();
        });
        this.transcriptions.set(sorted);
        this.loading.set(false);
        this.error.set(false);
      });
  }
}
